import { useState, useEffect, useCallback, useMemo } from "react";
import { adminApi, type EntitlementsResult } from "@/lib/superadmin";
import { TIER_RANK, type Tier } from "@/lib/access";
import {
  PageHeader,
  Card,
  ErrorBanner,
  EmptyState,
  PageSkeleton,
  GhostButton,
} from "../components/ui";

function inr(value: number): string {
  return `₹${value.toLocaleString("en-IN")}`;
}

const TIER_ORDER: Tier[] = ["starter", "growth", "pro"];

export default function EntitlementsPage() {
  const [data, setData] = useState<EntitlementsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await adminApi.entitlements.get());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load entitlements.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Order plans by rank so columns read starter → growth → pro.
  const plans = useMemo(
    () =>
      (data?.plans ?? [])
        .slice()
        .sort((a, b) => (a.rank ?? TIER_RANK[a.tier] ?? 99) - (b.rank ?? TIER_RANK[b.tier] ?? 99)),
    [data]
  );
  const tierOptions: Tier[] = plans.length ? (plans.map((p) => p.tier) as Tier[]) : TIER_ORDER;

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2200);
  };

  // Optimistic local patch + persist.
  const patchFeature = async (
    feature: string,
    patch: { minTier?: Tier; enabled?: boolean }
  ) => {
    setSavingKey(feature);
    setData((prev) =>
      prev
        ? {
            ...prev,
            features: prev.features.map((f) =>
              f.feature === feature
                ? {
                    ...f,
                    tier: patch.minTier ?? f.tier,
                    minTier: patch.minTier ?? f.minTier,
                    enabled: patch.enabled ?? f.enabled,
                  }
                : f
            ),
          }
        : prev
    );
    try {
      await adminApi.entitlements.setFeature({ feature, ...patch });
      flash("Saved");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed.");
      void load(); // re-sync on failure
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-[1400px] mx-auto">
      <PageHeader
        title="Feature Control"
        subtitle="Set the minimum plan each feature requires, or switch a feature off platform-wide"
        actions={
          <>
            {toast && (
              <span className="text-xs text-emerald-600 flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50">
                <i className="ri-check-line"></i>
                {toast}
              </span>
            )}
            <GhostButton onClick={() => void load()}>
              <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
              Refresh
            </GhostButton>
          </>
        }
      />

      <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-sky-50 border border-sky-100 text-sky-700 text-xs">
        <i className="ri-information-line text-sm shrink-0"></i>
        <span>
          Changes save instantly and apply on the merchant&apos;s next sign-in / refresh (web reads
          <code className="font-mono mx-1">ezydine.my_features()</code>). Per-tenant overrides live on each tenant&apos;s detail page.
        </span>
      </div>

      {error && <ErrorBanner message={error} onRetry={() => void load()} />}

      {loading ? (
        <PageSkeleton />
      ) : (
        <>
          {/* Plan price strip */}
          {plans.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans.map((p) => (
                <Card key={p.tier} className="p-4">
                  <p className="text-xs uppercase tracking-wide text-foreground-400 font-semibold">{p.label}</p>
                  <p className="text-2xl font-heading font-bold text-foreground-900 mt-1">{inr(p.price)}</p>
                </Card>
              ))}
            </div>
          )}

          {/* Editable feature table */}
          <Card padded={false} className="overflow-hidden">
            {data?.features.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-foreground-500 border-b border-background-200 bg-background-100/50">
                      <th className="px-4 py-3 font-medium">Feature</th>
                      <th className="px-4 py-3 font-medium">Minimum plan</th>
                      <th className="px-4 py-3 font-medium text-center">Enabled</th>
                      <th className="px-4 py-3 font-medium">Availability</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-100">
                    {data.features.map((f) => {
                      const minTier = (f.minTier ?? f.tier) as Tier;
                      const saving = savingKey === f.feature;
                      return (
                        <tr key={f.feature} className="hover:bg-background-100/40">
                          <td className="px-4 py-3 text-foreground-800 font-medium">
                            {f.label || f.feature}
                            <span className="ml-2 font-mono text-[11px] text-foreground-400">{f.feature}</span>
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={minTier}
                              disabled={saving || f.enabled === false}
                              onChange={(e) => void patchFeature(f.feature, { minTier: e.target.value as Tier })}
                              className="px-2.5 py-1.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 disabled:opacity-50 cursor-pointer"
                            >
                              {tierOptions.map((t) => (
                                <option key={t} value={t}>
                                  {plans.find((p) => p.tier === t)?.label ?? t}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => void patchFeature(f.feature, { enabled: !(f.enabled !== false) })}
                              disabled={saving}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${
                                f.enabled !== false ? "bg-emerald-500" : "bg-background-300"
                              }`}
                              title={f.enabled !== false ? "Disable platform-wide" : "Enable"}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  f.enabled !== false ? "translate-x-4" : "translate-x-0.5"
                                }`}
                              />
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            {f.enabled === false ? (
                              <span className="text-xs text-accent-600">Off for everyone</span>
                            ) : (
                              <div className="flex items-center gap-3">
                                {tierOptions.map((t) => {
                                  const unlocked = (TIER_RANK[t] ?? 99) >= (TIER_RANK[minTier] ?? 0);
                                  return (
                                    <span
                                      key={t}
                                      className={`inline-flex items-center gap-1 text-xs ${
                                        unlocked ? "text-emerald-600" : "text-foreground-300"
                                      }`}
                                    >
                                      <i className={unlocked ? "ri-check-line" : "ri-subtract-line"}></i>
                                      {plans.find((p) => p.tier === t)?.label ?? t}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState
                icon="ri-toggle-line"
                title="No feature mapping returned"
                hint="Feature entitlements will appear once configured in the backend."
              />
            )}
          </Card>
        </>
      )}
    </div>
  );
}
